import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  SxProps,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Supplier } from "features/order/types";
import { SuccessAlertContext } from "App";
import CustomTextField from "./CustomTextField";
import { GenuineIncrementalSearchElement, IncrementalSearchElement } from "./Common";
import SurroundLine from "./SurroundLine";

const incrementalSearchOptionLength = 10;
type Props = {
  open: boolean;
  onClose: () => void;
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  isLoading: boolean;
  productCd: string;
  janCd: string;
  classNameOption: readonly string[];
  className: string;
  productSrc: string;
  brand: string;
  productName: string;
  widthOptions: number[];
  width?: number;
  oblatenessOptions: number[];
  oblateness?: number;
  tireRimDmtrOptions: number[];
  tireRimDmtr?: number;
  wheelRimDmtrOptions: number[];
  wheelRimDmtr?: number;
  rimWidthOptions: number[];
  rimWidth?: number;
  holeNmbrOptions: number[];
  holeNmbr?: number;
  PCDOptions: number[];
  PCD?: number;
  isUnsetUnitPrice: boolean;
  supplierOptions: Supplier[];
  onSearch: () => Promise<void>;
  setProductCd: Dispatch<SetStateAction<string>>;
  setJanCd: Dispatch<SetStateAction<string>>;
  setClassName: Dispatch<SetStateAction<string>>;
  setProductSrc: Dispatch<SetStateAction<string>>;
  setBrand: Dispatch<SetStateAction<string>>;
  setProductName: Dispatch<SetStateAction<string>>;
  setWidth: Dispatch<SetStateAction<number | undefined>>;
  setOblateness: Dispatch<SetStateAction<number | undefined>>;
  setTireRimDmtr: Dispatch<SetStateAction<number | undefined>>;
  setWheelRimDmtr: Dispatch<SetStateAction<number | undefined>>;
  setRimWidth: Dispatch<SetStateAction<number | undefined>>;
  setHoleNmbr: Dispatch<SetStateAction<number | undefined>>;
  setPCD: Dispatch<SetStateAction<number | undefined>>;
  setIsUnsetUnitPrice: Dispatch<SetStateAction<boolean>>;
};

const styles: { [key: string]: SxProps } = {
  searchRowBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
};

type SelectFormProps = {
  id: string;
  label: string;
  optionList: string[];
  value: string;
  setValue: Dispatch<SetStateAction<string>> | Function;
  required?: boolean;
  disabled?: boolean;
  isContainBlank?: boolean;
};

/**
 * 選択フォーム
 * @param root0 選択フォームのプロップス
 * @returns JSX.Element
 */
function SelectForm({
  id,
  label,
  optionList,
  value,
  setValue,
  required,
  disabled,
  isContainBlank,
}: SelectFormProps) {
  return (
    <Box width="50%" m={0}>
      <Box p={1}>
        <FormControl sx={{ minWidth: 160 }} required={required}>
          <InputLabel
            id={`${id}-label`}
            sx={{
              top: "-6px",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -4px) scale(0.75)",
              },
              "& .MuiFormLabel-asterisk": {
                color: "var(--red-800)",
              },
            }}
          >
            {label}
          </InputLabel>
          <Select
            labelId={`${id}-label`}
            label={label}
            id={id}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ height: "40px" }}
            disabled={disabled}
          >
            {isContainBlank && (
              // eslint-disable-next-line no-irregular-whitespace, react/self-closing-comp
              <MenuItem key="" value="">
                &emsp;
              </MenuItem>
            )}
            {optionList.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

/**
 * Product Search Dialog 商品の絞込検索
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
function ProductSettingSearchDialog({
  open,
  isFastLoading,
  onClose,
  isLoading,
  getMoreStockList,
  productCd,
  janCd,
  classNameOption,
  className,
  productSrc,
  brand,
  productName,
  widthOptions,
  width,
  oblatenessOptions,
  oblateness,
  tireRimDmtrOptions,
  tireRimDmtr,
  wheelRimDmtrOptions,
  wheelRimDmtr,
  rimWidthOptions,
  rimWidth,
  holeNmbrOptions,
  holeNmbr,
  PCDOptions,
  PCD,
  isUnsetUnitPrice,
  supplierOptions,
  onSearch,
  setProductCd,
  setJanCd,
  setClassName,
  setProductSrc,
  setBrand,
  setProductName,
  setWidth,
  setOblateness,
  setTireRimDmtr,
  setWheelRimDmtr,
  setRimWidth,
  setHoleNmbr,
  setPCD,
  setIsUnsetUnitPrice,
}: Props) {
  const { showAlert } = useContext(SuccessAlertContext);
  const searchElementRow = () => {
    const searchElements = [
      <Box key="productCd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productCd"
          type="text"
          label="商品コード"
          text={productCd}
          setText={setProductCd}
          maxLength={100}
          size="small"
          isNumericText
        />
      </Box>,
      <Box key="janCd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="janCd"
          type="text"
          label="JANコード"
          text={janCd}
          setText={setJanCd}
          maxLength={100}
          size="small"
        />
      </Box>,
      // タイヤ/ホイール 分類
      <IncrementalSearchElement
        key="className"
        label="大分類"
        options={classNameOption}
        value={className}
        onChange={(val: string) => setClassName(val)}
        optionLength={incrementalSearchOptionLength}
        required
      />,
      <GenuineIncrementalSearchElement
        key="productSrc"
        label="仕入先名"
        options={supplierOptions.map(so => so.supplierName)}
        value={productSrc}
        onChange={setProductSrc}
        optionLength={incrementalSearchOptionLength}
        isBlankNoOptions
      />,
      <Box key="brand" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="brand"
          type="text"
          label="ブランド"
          text={brand}
          setText={setBrand}
          maxLength={100}
          size="small"
        />
      </Box>,
      <Box key="productName" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productName"
          type="text"
          label="商品名"
          text={productName}
          setText={setProductName}
          maxLength={100}
          size="small"
        />
      </Box>,
      <FormControlLabel control={<Checkbox color="secondary" checked={isUnsetUnitPrice} onChange={(e) => setIsUnsetUnitPrice(e.target.checked)} />} label="仕切単価未設定" />,
    ];

    const len = Math.ceil(searchElements.length / 2);
    const couples: JSX.Element[] = [];
    for (let i = 0; i < len; i += 1) {
      const element1 = searchElements[i * 2];
      let element2: JSX.Element | undefined;
      if (i * 2 + 1 < searchElements.length) {
        element2 = searchElements[i * 2 + 1];
      }
      couples.push(
        <Box sx={styles.searchRowBox}>
          {element1}
          {element2 ?? <> </>}
        </Box>,
      );
    }

    return (
      <div>
        {couples}
        {className === "タイヤ" && (
          <SurroundLine title="タイヤサイズ" sx={{ padding: 2, marginTop: 2 }}>
            <Box display="flex">
              <Grid container spacing={2} alignItems="center">
                <Grid item sm={6}>
                  <IncrementalSearchElement
                    key="width"
                    label="幅"
                    options={widthOptions.map((t) => `${t}`)}
                    value={`${width ?? ""}`}
                    onChange={(val: string) => {
                      setWidth(val === "" ? undefined : Number(val));
                    }}
                    optionLength={incrementalSearchOptionLength}
                    width="222px"
                    isContainBlank
                  />
                </Grid>
                <Grid item sm={6}>
                  <IncrementalSearchElement
                    key="oblateness"
                    label="扁平率"
                    options={oblatenessOptions.map((t) => `${t}`)}
                    value={`${oblateness ?? ""}`}
                    onChange={(val: string) => {
                      setOblateness(val === "" ? undefined : Number(val));
                    }}
                    optionLength={incrementalSearchOptionLength}
                    width="222px"
                    isContainBlank
                  />
                </Grid>
                <Grid item sm={6}>
                  <IncrementalSearchElement
                    key="tireRimDmtr"
                    label="タイヤリム径"
                    options={tireRimDmtrOptions.map((t) => `${t}`)}
                    value={`${tireRimDmtr ?? ""}`}
                    onChange={(val: string) => {
                      setTireRimDmtr(val === "" ? undefined : Number(val));
                    }}
                    optionLength={incrementalSearchOptionLength}
                    width="222px"
                    isContainBlank
                  />
                </Grid>
              </Grid>
            </Box>
          </SurroundLine>
        )}
        {className === "ホイール" && (
          <SurroundLine title="ホイールサイズ" sx={{ padding: 2, marginTop: 2 }}>
            <Box display="flex">
              <Grid container spacing={2} alignItems="center">
                <Grid item sm={6}>
                  <IncrementalSearchElement
                    key="wheelRimDmtr"
                    label="ホイールリム径"
                    options={wheelRimDmtrOptions.map((t) => `${t}`)}
                    value={`${wheelRimDmtr ?? ""}`}
                    onChange={(val: string) => {
                      setWheelRimDmtr(val === "" ? undefined : Number(val));
                    }}
                    optionLength={incrementalSearchOptionLength}
                    width="222px"
                    isContainBlank
                  />
                </Grid>
                <Grid item sm={6}>
                  <IncrementalSearchElement
                    key="rimWidth"
                    label="リム幅"
                    options={rimWidthOptions.map((t) => `${t}`)}
                    value={`${rimWidth ?? ""}`}
                    onChange={(val: string) => {
                      setRimWidth(val === "" ? undefined : Number(val));
                    }}
                    optionLength={incrementalSearchOptionLength}
                    width="222px"
                    isContainBlank
                  />
                </Grid>
                <Grid item sm={6}>
                  <IncrementalSearchElement
                    key="holeNmbr"
                    label="ホール数"
                    options={holeNmbrOptions.map((t) => `${t}`)}
                    value={`${holeNmbr ?? ""}`}
                    onChange={(val: string) => {
                      setHoleNmbr(val === "" ? undefined : Number(val));
                    }}
                    optionLength={incrementalSearchOptionLength}
                    width="222px"
                  />
                </Grid>
                <Grid item sm={6}>
                  <IncrementalSearchElement
                    key="PCD"
                    label="PCD"
                    options={PCDOptions.map((t) => `${t.toFixed(2)}`)}
                    value={`${PCD ? PCD.toFixed(2) : ""}`}
                    onChange={(val: string) => {
                      setPCD(val === "" ? undefined : Number(val));
                    }}
                    optionLength={incrementalSearchOptionLength}
                    width="222px"
                    isContainBlank
                  />
                </Grid>
              </Grid>
            </Box>
          </SurroundLine>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box p={2}>
        <DialogTitle>絞り込み検索</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
              }}
            >
              <CircularProgress size={80} />
            </Box>
          )}
          {searchElementRow()}
        </DialogContent>
        <DialogActions>
          <Button variant="custom-outlined" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if ((open && className === "") || className === null) {
                showAlert("大分類を選択してください。", "error");
                return;
              }
              onSearch();
              onClose();
            }}
            disabled={isLoading}
          >
            {isLoading ? "検索中..." : "検索"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default React.memo(ProductSettingSearchDialog);
